import React, { useState, useEffect, useCallback } from 'react';
import { useTranslationReady, TranslationLoadingScreen } from '../../../hooks/useTranslationReady';
import { useTheme } from '../../../contexts/ThemeContext';
import { api } from '../../../utils/api';
import { loadEntityConfig } from '../../../utils/entityConfig';
import {
  Tag,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  RefreshCw,
  Merge,
  Palette,
  FileText,
  Layers,
  AlertCircle,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Eye,
  ExternalLink,
} from 'lucide-react';

const TagManager = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'tagManager.title',
    'tagManager.subtitle',
    'tagManager.searchPlaceholder',
    'tagManager.createTag',
    'tagManager.collectTags',
    'tagManager.collecting',
    'tagManager.clearZeroUsage',
    'tagManager.mergeTags',
    'common.refresh',
    'common.edit',
    'common.delete',
    'common.cancel',
    'common.save'
  ]);
  const { isDark } = useTheme();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTab, setActiveTab] = useState('tags');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [viewingTag, setViewingTag] = useState(null);
  const [tagUsageData, setTagUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const [autoRules, setAutoRules] = useState([]);
  const [autoRuleLoading, setAutoRuleLoading] = useState(false);
  const [autoRuleEntityFilter, setAutoRuleEntityFilter] = useState('All');
  const [showAutoRuleModal, setShowAutoRuleModal] = useState(false);
  const [editingAutoRule, setEditingAutoRule] = useState(null);
  const [autoRuleForm, setAutoRuleForm] = useState({
    name: '',
    entityType: 'Lead',
    enabled: true,
    conditions: { operator: 'AND', conditions: [{ field: '', operator: 'equals', value: '' }] },
    tags: []
  });
  const [autoRuleTagsInput, setAutoRuleTagsInput] = useState('');
  const [entityFields, setEntityFields] = useState({});
  const [autoRunEntityType, setAutoRunEntityType] = useState('Lead');

  const [tagForm, setTagForm] = useState({
    name: '',
    backgroundColor: '#F3F4F6',
    textColor: '#374151',
    borderColor: '#D1D5DB',
    fontWeight: 'normal',
  });

  const [mergeTarget, setMergeTarget] = useState('');
  const [collecting, setCollecting] = useState(false);
  const [collectionStats, setCollectionStats] = useState(null);
  const [updateStats, setUpdateStats] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    loadTags();
  }, []);

  const entityOptions = [
    { value: 'Customer', label: safeT('tagManager.entity.customer', 'Customers') },
    { value: 'Lead', label: safeT('tagManager.entity.lead', 'Leads') },
    { value: 'Opportunity', label: safeT('tagManager.entity.opportunity', 'Opportunities') },
    { value: 'Activity', label: safeT('tagManager.entity.activity', 'Activities') },
    { value: 'Notes', label: safeT('tagManager.entity.notes', 'Notes') },
    { value: 'Attachment', label: safeT('tagManager.entity.attachment', 'Attachments') },
    { value: 'Knowledge', label: safeT('tagManager.entity.knowledge', 'Knowledge Articles') }
  ];

  const operatorOptions = [
    { value: 'equals', label: safeT('tagManager.operator.equals', 'Equals') },
    { value: 'notEquals', label: safeT('tagManager.operator.notEquals', 'Not Equals') },
    { value: 'contains', label: safeT('tagManager.operator.contains', 'Contains') },
    { value: 'notContains', label: safeT('tagManager.operator.notContains', 'Not Contains') },
    { value: 'startsWith', label: safeT('tagManager.operator.startsWith', 'Starts With') },
    { value: 'endsWith', label: safeT('tagManager.operator.endsWith', 'Ends With') },
    { value: 'greaterThan', label: safeT('tagManager.operator.greaterThan', 'Greater Than') },
    { value: 'greaterOrEqual', label: safeT('tagManager.operator.greaterOrEqual', 'Greater or Equal') },
    { value: 'lessThan', label: safeT('tagManager.operator.lessThan', 'Less Than') },
    { value: 'lessOrEqual', label: safeT('tagManager.operator.lessOrEqual', 'Less or Equal') },
    { value: 'isEmpty', label: safeT('tagManager.operator.isEmpty', 'Is Empty') },
    { value: 'isNotEmpty', label: safeT('tagManager.operator.isNotEmpty', 'Is Not Empty') },
    { value: 'inList', label: safeT('tagManager.operator.inList', 'In List') },
    { value: 'notInList', label: safeT('tagManager.operator.notInList', 'Not In List') }
  ];

  const parseJsonSafe = (value, fallback) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const buildEntityFields = (entityConfig) => {
    const fields = entityConfig?.fields || {};
    return Object.entries(fields).map(([key, fieldConfig]) => ({
      key,
      label: fieldConfig?.displayName || fieldConfig?.label || key
    })).sort((a, b) => a.label.localeCompare(b.label));
  };

  const loadEntityFields = async (entityType) => {
    try {
      const tenantCode = api.getCurrentTenantCode?.() || null;
      const config = await loadEntityConfig(entityType.toLowerCase(), tenantCode);
      if (!config) {
        setEntityFields((prev) => ({ ...prev, [entityType]: [] }));
        return;
      }
      const fields = buildEntityFields(config);
      setEntityFields((prev) => ({ ...prev, [entityType]: fields }));
    } catch (err) {
      console.error('Failed to load entity config for auto tag rules:', err);
      setEntityFields((prev) => ({ ...prev, [entityType]: [] }));
    }
  };

  const loadAutoRules = async () => {
    setAutoRuleLoading(true);
    try {
      const response = await api.get('/tags/auto-rules');
      const rules = (response.rules || []).map((rule) => ({
        id: rule.RuleID ?? rule.id,
        name: rule.RuleName ?? rule.name,
        entityType: rule.EntityType ?? rule.entityType,
        enabled: Boolean(rule.Enabled ?? rule.enabled),
        conditions: parseJsonSafe(rule.ConditionsJson ?? rule.conditions, { operator: 'AND', conditions: [] }),
        tags: parseJsonSafe(rule.TagsJson ?? rule.tags, [])
      }));
      setAutoRules(rules);
    } catch (err) {
      console.error('Failed to load auto tag rules:', err);
      setError(err.message || 'Failed to load auto tag rules');
    } finally {
      setAutoRuleLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'autoRules') {
      loadAutoRules();
      if (!entityFields[autoRuleForm.entityType]) {
        loadEntityFields(autoRuleForm.entityType);
      }
    }
  }, [activeTab, autoRuleForm.entityType]);

  const resetAutoRuleForm = () => {
    setAutoRuleForm({
      name: '',
      entityType: 'Lead',
      enabled: true,
      conditions: { operator: 'AND', conditions: [{ field: '', operator: 'equals', value: '' }] },
      tags: []
    });
    setAutoRuleTagsInput('');
  };

  const openAutoRuleModal = (rule = null) => {
    if (rule) {
      setEditingAutoRule(rule);
      setAutoRuleForm({
        name: rule.name,
        entityType: rule.entityType,
        enabled: rule.enabled,
        conditions: rule.conditions || { operator: 'AND', conditions: [] },
        tags: rule.tags || []
      });
      setAutoRuleTagsInput((rule.tags || []).join(', '));
    } else {
      setEditingAutoRule(null);
      resetAutoRuleForm();
    }
    setShowAutoRuleModal(true);
  };

  const handleSaveAutoRule = async () => {
    if (!autoRuleForm.name.trim()) {
      setError('Rule name is required');
      return;
    }
    if (!autoRuleForm.entityType) {
      setError('Entity type is required');
      return;
    }
    const hasInvalidCondition = (group) => {
      if (!group || !Array.isArray(group.conditions)) return true;
      return group.conditions.some((condition) => {
        if (condition.conditions) return hasInvalidCondition(condition);
        if (!condition.field || !condition.operator) return true;
        if (!['isEmpty', 'isNotEmpty'].includes(condition.operator) && (condition.value === undefined || condition.value === null || String(condition.value).trim() === '')) {
          return true;
        }
        return false;
      });
    };
    if (hasInvalidCondition(autoRuleForm.conditions)) {
      setError('Please complete all rule conditions before saving');
      return;
    }
    const tags = autoRuleTagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const payload = {
      name: autoRuleForm.name.trim(),
      entityType: autoRuleForm.entityType,
      enabled: autoRuleForm.enabled,
      conditions: autoRuleForm.conditions,
      tags
    };
    try {
      if (editingAutoRule) {
        await api.put(`/tags/auto-rules/${editingAutoRule.id}`, payload);
      } else {
        await api.post('/tags/auto-rules', payload);
      }
      await loadAutoRules();
      setShowAutoRuleModal(false);
      resetAutoRuleForm();
      setEditingAutoRule(null);
    } catch (err) {
      console.error('Failed to save auto tag rule:', err);
      setError(err.message || 'Failed to save auto tag rule');
    }
  };

  const handleToggleAutoRule = async (rule) => {
    try {
      await api.put(`/tags/auto-rules/${rule.id}`, { enabled: !rule.enabled });
      await loadAutoRules();
    } catch (err) {
      console.error('Failed to update auto tag rule:', err);
      setError(err.message || 'Failed to update auto tag rule');
    }
  };

  const handleDeleteAutoRule = async (ruleId) => {
    if (!window.confirm('Delete this auto tag rule?')) return;
    try {
      await api.delete(`/tags/auto-rules/${ruleId}`);
      await loadAutoRules();
    } catch (err) {
      console.error('Failed to delete auto tag rule:', err);
      setError(err.message || 'Failed to delete auto tag rule');
    }
  };

  const handleRunAutoRules = async () => {
    try {
      await api.post('/tags/auto-rules/run', { entityType: autoRunEntityType });
    } catch (err) {
      console.error('Failed to run auto tag rules:', err);
      setError(err.message || 'Failed to run auto tag rules');
    }
  };

  const createEmptyCondition = () => ({ field: '', operator: 'equals', value: '' });
  const createEmptyGroup = () => ({ operator: 'AND', conditions: [createEmptyCondition()] });
  const cloneConditions = (conditions) => JSON.parse(JSON.stringify(conditions || createEmptyGroup()));

  const updateGroupOperator = (path, value) => {
    setAutoRuleForm((prev) => {
      const nextConditions = cloneConditions(prev.conditions);
      let node = nextConditions;
      path.forEach((idx) => {
        node = node.conditions[idx];
      });
      node.operator = value;
      return { ...prev, conditions: nextConditions };
    });
  };

  const updateConditionAtPath = (path, updater) => {
    setAutoRuleForm((prev) => {
      const nextConditions = cloneConditions(prev.conditions);
      let node = nextConditions;
      for (let i = 0; i < path.length - 1; i++) {
        node = node.conditions[path[i]];
      }
      const idx = path[path.length - 1];
      node.conditions[idx] = updater(node.conditions[idx]);
      return { ...prev, conditions: nextConditions };
    });
  };

  const addConditionAtPath = (path) => {
    setAutoRuleForm((prev) => {
      const nextConditions = cloneConditions(prev.conditions);
      let node = nextConditions;
      path.forEach((idx) => {
        node = node.conditions[idx];
      });
      node.conditions.push(createEmptyCondition());
      return { ...prev, conditions: nextConditions };
    });
  };

  const addGroupAtPath = (path) => {
    setAutoRuleForm((prev) => {
      const nextConditions = cloneConditions(prev.conditions);
      let node = nextConditions;
      path.forEach((idx) => {
        node = node.conditions[idx];
      });
      node.conditions.push(createEmptyGroup());
      return { ...prev, conditions: nextConditions };
    });
  };

  const removeConditionAtPath = (path) => {
    setAutoRuleForm((prev) => {
      const nextConditions = cloneConditions(prev.conditions);
      let node = nextConditions;
      for (let i = 0; i < path.length - 1; i++) {
        node = node.conditions[path[i]];
      }
      node.conditions.splice(path[path.length - 1], 1);
      if (node.conditions.length === 0) {
        node.conditions.push(createEmptyCondition());
      }
      return { ...prev, conditions: nextConditions };
    });
  };

  const renderConditionGroup = (group, path = []) => {
    const fields = entityFields[autoRuleForm.entityType] || [];
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3 bg-white dark:bg-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">{safeT('tagManager.ruleGroup', 'Group')}</span>
          <select
            value={group.operator}
            onChange={(e) => updateGroupOperator(path, e.target.value)}
            className="px-2 py-1 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900"
          >
            <option value="AND">{safeT('tagManager.and', 'AND')}</option>
            <option value="OR">{safeT('tagManager.or', 'OR')}</option>
          </select>
          <button
            type="button"
            onClick={() => addConditionAtPath(path)}
            className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
          >
            {safeT('tagManager.addCondition', 'Add Condition')}
          </button>
          <button
            type="button"
            onClick={() => addGroupAtPath(path)}
            className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
          >
            {safeT('tagManager.addGroup', 'Add Group')}
          </button>
          {path.length > 0 && (
            <button
              type="button"
              onClick={() => removeConditionAtPath(path)}
              className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
            >
              {safeT('tagManager.removeGroup', 'Remove Group')}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {group.conditions.map((condition, index) => {
            const nextPath = [...path, index];
            if (condition.conditions) {
              return (
                <div key={nextPath.join('-')} className="ml-4">
                  {renderConditionGroup(condition, nextPath)}
                </div>
              );
            }
            const operator = condition.operator || 'equals';
            const needsValue = !['isEmpty', 'isNotEmpty'].includes(operator);
            return (
              <div key={nextPath.join('-')} className="flex flex-wrap items-center gap-2">
                {fields.length > 0 ? (
                  <select
                    value={condition.field}
                    onChange={(e) =>
                      updateConditionAtPath(nextPath, (prev) => ({ ...prev, field: e.target.value }))
                    }
                    className="min-w-[180px] px-2 py-1 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900"
                  >
                    <option value="">{safeT('tagManager.selectField', 'Select field')}</option>
                    {fields.map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={condition.field}
                    onChange={(e) =>
                      updateConditionAtPath(nextPath, (prev) => ({ ...prev, field: e.target.value }))
                    }
                    className="min-w-[180px] px-2 py-1 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900"
                    placeholder={safeT('tagManager.fieldName', 'Field name')}
                  />
                )}
                <select
                  value={operator}
                  onChange={(e) =>
                    updateConditionAtPath(nextPath, (prev) => ({ ...prev, operator: e.target.value }))
                  }
                  className="min-w-[150px] px-2 py-1 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900"
                >
                  {operatorOptions.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
                {needsValue && (
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) =>
                      updateConditionAtPath(nextPath, (prev) => ({ ...prev, value: e.target.value }))
                    }
                    className="min-w-[180px] px-2 py-1 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900"
                    placeholder={safeT('tagManager.value', 'Value')}
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeConditionAtPath(nextPath)}
                  className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
                >
                  {safeT('common.delete', 'Delete')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/tags');
      setTags(response.tags || []);
    } catch (err) {
      console.error('Failed to load tags:', err);
      setError(err.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!tagForm.name.trim()) {
      setError('Tag name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.post('/tags', tagForm);
      await loadTags();
      setShowCreateModal(false);
      resetTagForm();
    } catch (err) {
      console.error('Failed to create tag:', err);
      setError(err.message || 'Failed to create tag');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !tagForm.name.trim()) {
      setError('Tag name is required');
      return;
    }

    // Check if the new name matches an existing tag (excluding the current tag)
    const newName = tagForm.name.trim();
    const existingTag = tags.find(
      (tag) => tag.id !== editingTag.id && tag.name.toLowerCase() === newName.toLowerCase()
    );

    // If name changed and matches an existing tag, ask to merge
    if (existingTag && editingTag.name.toLowerCase() !== newName.toLowerCase()) {
      const shouldMerge = window.confirm(
        `A tag named "${newName}" already exists. Do you want to merge "${editingTag.name}" into "${newName}"? This will move all associations from "${editingTag.name}" to "${newName}" and delete "${editingTag.name}".`
      );

      if (shouldMerge) {
        // Merge the tags
        setLoading(true);
        setError(null);
        setUpdateStats(null);
        try {
          await api.post('/tags/merge', {
            sourceTagIds: [editingTag.id],
            targetTagId: existingTag.id,
          });
          await loadTags();
          setShowEditModal(false);
          setEditingTag(null);
          resetTagForm();
        } catch (err) {
          console.error('Failed to merge tags:', err);
          setError(err.message || 'Failed to merge tags');
        } finally {
          setLoading(false);
        }
        return;
      } else {
        // User cancelled merge, don't proceed with rename
        setError('Tag rename cancelled. Please choose a different name or confirm merge.');
        return;
      }
    }

    // Check if name has changed
    const nameChanged = editingTag.name.toLowerCase() !== newName.toLowerCase();
    
    // If name changed, ask for confirmation
    if (nameChanged) {
      const shouldUpdate = window.confirm(
        `Are you sure you want to rename "${editingTag.name}" to "${newName}"?\n\nThis will update the tag name in all entities and attachments where it is used.`
      );
      
      if (!shouldUpdate) {
        return;
      }
    }

    // Normal update (no duplicate name)
    setLoading(true);
    setError(null);
    setUpdateStats(null);
    try {
      const response = await api.put(`/tags/${editingTag.id}`, tagForm);
      
      // Display update statistics if available
      if (response.stats) {
        setUpdateStats({
          tagName: newName,
          totalUpdated: response.stats.totalUpdated || 0,
          entities: response.stats.entities || 0,
          attachments: response.stats.attachments || 0,
          salesNotes: response.stats.salesNotes || 0,
          knowledge: response.stats.knowledge || 0,
        });
      }
      
      await loadTags();
      setShowEditModal(false);
      setEditingTag(null);
      resetTagForm();
    } catch (err) {
      console.error('Failed to update tag:', err);
      setError(err.message || 'Failed to update tag');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag? This will remove it from all associated entities and attachments.')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.delete(`/tags/${tagId}`);
      await loadTags();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      setError(err.message || 'Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  const handleMergeTags = async () => {
    if (selectedTags.length < 2) {
      setError('Please select at least 2 tags to merge');
      return;
    }

    if (!mergeTarget) {
      setError('Please select a target tag');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.post('/tags/merge', {
        sourceTagIds: selectedTags,
        targetTagId: mergeTarget,
      });
      await loadTags();
      setShowMergeModal(false);
      setSelectedTags([]);
      setMergeTarget('');
    } catch (err) {
      console.error('Failed to merge tags:', err);
      setError(err.message || 'Failed to merge tags');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setTagForm({
      name: tag.name,
      backgroundColor: tag.backgroundColor || '#F3F4F6',
      textColor: tag.textColor || '#374151',
      borderColor: tag.borderColor || '#D1D5DB',
      fontWeight: tag.fontWeight || 'normal',
    });
    setShowEditModal(true);
  };

  const resetTagForm = () => {
    setTagForm({
      name: '',
      backgroundColor: '#F3F4F6',
      textColor: '#374151',
      borderColor: '#D1D5DB',
      fontWeight: 'normal',
    });
  };

  const handleCollectTags = async () => {
    setCollecting(true);
    setError(null);
    setCollectionStats(null);
    try {
      const response = await api.post('/tags/collect');
      setCollectionStats(response.stats);
      await loadTags(); // Reload tags after collection
    } catch (err) {
      console.error('Failed to collect tags:', err);
      setError(err.message || 'Failed to collect tags from existing data');
    } finally {
      setCollecting(false);
    }
  };

  const handleClearZeroUsageTags = async () => {
    const zeroUsageTags = tags.filter((tag) => !tag.usageCount || tag.usageCount === 0);
    
    if (zeroUsageTags.length === 0) {
      setError('No tags with 0 usage found');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${zeroUsageTags.length} tag(s) with 0 usage? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const deletePromises = zeroUsageTags.map((tag) => api.delete(`/tags/${tag.id}`));
      await Promise.all(deletePromises);
      await loadTags();
    } catch (err) {
      console.error('Failed to clear zero usage tags:', err);
      setError(err.message || 'Failed to clear zero usage tags');
    } finally {
      setLoading(false);
    }
  };

  const toggleTagSelection = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleViewTagUsage = async (tag) => {
    setViewingTag(tag);
    setShowUsageModal(true);
    setLoadingUsage(true);
    setTagUsageData(null);
    
    try {
      const response = await api.get(`/tags/${tag.id}/usage`);
      setTagUsageData(response);
    } catch (err) {
      console.error('Failed to load tag usage:', err);
      setError(err.message || 'Failed to load tag usage');
    } finally {
      setLoadingUsage(false);
    }
  };

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedTags = [...filteredTags].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue, bValue;

    switch (sortColumn) {
      case 'name':
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
        break;
      case 'usage':
        aValue = a.usageCount || 0;
        bValue = b.usageCount || 0;
        break;
      case 'created':
        aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const zeroUsageTagsCount = tags.filter((tag) => !tag.usageCount || tag.usageCount === 0).length;

  const filteredAutoRules = autoRules.filter((rule) =>
    autoRuleEntityFilter === 'All' ? true : rule.entityType === autoRuleEntityFilter
  );

  // Show loading state while translations are loading
  if (!translationsReady || !ready) {
    return <TranslationLoadingScreen language={i18n.language} />;
  }

  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-300' : 'text-gray-600';
  const bgPrimary = isDark ? 'bg-gray-800' : 'bg-white';
  const bgSecondary = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${bgSecondary}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-purple-600 text-white shadow-md">
            <Tag className="h-6 w-6" />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold ${textPrimary}`}>
              {safeT('tagManager.title', 'Tag Manager')}
            </h1>
            <p className={`text-xs sm:text-sm md:text-base ${textSecondary}`}>
              {safeT('tagManager.subtitle', 'Manage tags across all entities and attachments')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-2">
        <button
          onClick={() => setActiveTab('tags')}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            activeTab === 'tags'
              ? 'bg-purple-600 text-white border-purple-600'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
          }`}
        >
          {safeT('tagManager.tab.tags', 'Tags')}
        </button>
        <button
          onClick={() => setActiveTab('autoRules')}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            activeTab === 'autoRules'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
          }`}
        >
          {safeT('tagManager.tab.autoTags', 'Auto Tags')}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {activeTab === 'tags' && (
        <>
      {/* Collection Stats */}
      {collectionStats && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/40 dark:bg-emerald-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                {safeT('tagManager.collectionComplete', 'Tag Collection Complete')}
              </h3>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-emerald-700 dark:text-emerald-200">
                <div>
                  <span className="font-medium">Attachments:</span> {collectionStats.attachments}
                </div>
                <div>
                  <span className="font-medium">Notes:</span> {collectionStats.salesNotes}
                </div>
                <div>
                  <span className="font-medium">Knowledge:</span> {collectionStats.knowledge}
                </div>
                <div>
                  <span className="font-medium">Customers:</span> {collectionStats.customers}
                </div>
                <div>
                  <span className="font-medium">Leads:</span> {collectionStats.leads}
                </div>
                <div>
                  <span className="font-medium">Opportunities:</span> {collectionStats.opportunities}
                </div>
                <div>
                  <span className="font-medium">Activities:</span> {collectionStats.activities}
                </div>
                <div className="font-bold">
                  <span className="font-medium">New Tags:</span> {collectionStats.newTagsCreated}
                </div>
              </div>
              <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-300">
                Found {collectionStats.totalTagsFound} unique tags, created {collectionStats.newTagsCreated} new tag records.
              </p>
            </div>
            <button
              onClick={() => setCollectionStats(null)}
              className="text-emerald-500 hover:text-emerald-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Update Stats */}
      {updateStats && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-500/40 dark:bg-blue-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                {safeT('tagManager.updateComplete', 'Tag Update Complete')}
              </h3>
              <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
                Successfully updated tag "<span className="font-semibold">{updateStats.tagName}</span>"
              </p>
              {updateStats.totalUpdated > 0 ? (
                <>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-blue-700 dark:text-blue-200">
                    {updateStats.entities > 0 && (
                      <div>
                        <span className="font-medium">Entities:</span> {updateStats.entities}
                      </div>
                    )}
                    {updateStats.attachments > 0 && (
                      <div>
                        <span className="font-medium">Attachments:</span> {updateStats.attachments}
                      </div>
                    )}
                    {updateStats.salesNotes > 0 && (
                      <div>
                        <span className="font-medium">Notes:</span> {updateStats.salesNotes}
                      </div>
                    )}
                    {updateStats.knowledge > 0 && (
                      <div>
                        <span className="font-medium">Knowledge:</span> {updateStats.knowledge}
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-blue-600 dark:text-blue-300">
                    Total items updated: {updateStats.totalUpdated}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-xs text-blue-600 dark:text-blue-300">
                  Tag styling updated. No name change detected.
                </p>
              )}
            </div>
            <button
              onClick={() => setUpdateStats(null)}
              className="text-blue-500 hover:text-blue-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className={`rounded-xl border ${borderColor} ${bgPrimary} p-4 shadow-sm mb-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={safeT('tagManager.searchPlaceholder', 'Search tags...')}
              className={`w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} text-sm focus:ring-2 focus:ring-purple-500`}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              <span className="text-xs sm:text-sm">{safeT('tagManager.createTag', 'Create Tag')}</span>
            </button>

            <button
              onClick={handleCollectTags}
              disabled={collecting || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 disabled:opacity-50"
              title="Scan existing data and import tags from Attachments, Notes, Knowledge, etc."
            >
              <Layers className={`h-4 w-4 ${collecting ? 'animate-spin' : ''}`} />
              <span className="text-xs sm:text-sm">{collecting ? safeT('tagManager.collecting', 'Collecting...') : safeT('tagManager.collectTags', 'Collect Tags')}</span>
            </button>

            {zeroUsageTagsCount > 0 && (
              <button
                onClick={handleClearZeroUsageTags}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-orange-700 disabled:opacity-50"
                title={`Delete ${zeroUsageTagsCount} tag(s) with 0 usage`}
              >
                <Trash2 className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('tagManager.clearZeroUsage', 'Clear 0 Usage')}</span> ({zeroUsageTagsCount})
              </button>
            )}

            {selectedTags.length >= 2 && (
              <button
                onClick={() => setShowMergeModal(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
              >
                <Merge className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('tagManager.mergeTags', 'Merge Tags')}</span> ({selectedTags.length})
              </button>
            )}

            <button
              onClick={loadTags}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-xs sm:text-sm">{safeT('common.refresh', 'Refresh')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className={`rounded-xl border ${borderColor} ${bgPrimary} p-4 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-500 font-medium">
                <span className="text-xs sm:text-sm">{safeT('tagManager.totalTags', 'Total Tags')}</span>
              </p>
              <p className={`text-2xl font-bold ${textPrimary} mt-1`}>
                {tags.length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Tag className="h-6 w-6 text-purple-500" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl border ${borderColor} ${bgPrimary} p-4 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-500 font-medium">
                <span className="text-xs sm:text-sm">{safeT('tagManager.totalUsage', 'Total Usage')}</span>
              </p>
              <p className={`text-2xl font-bold ${textPrimary} mt-1`}>
                {tags.reduce((sum, tag) => sum + (tag.usageCount || 0), 0)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Layers className="h-6 w-6 text-blue-500" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl border ${borderColor} ${bgPrimary} p-4 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-500 font-medium">
                <span className="text-xs sm:text-sm">{safeT('tagManager.selected', 'Selected')}</span>
              </p>
              <p className={`text-2xl font-bold ${textPrimary} mt-1`}>
                {selectedTags.length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10">
              <CheckCircle className="h-6 w-6 text-emerald-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Tags List */}
      <div className={`rounded-xl border ${borderColor} ${bgPrimary} shadow-sm overflow-hidden`}>
        {loading && tags.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
            <span className="ml-2 text-sm text-purple-600">
              <span className="text-xs sm:text-sm">{safeT('tagManager.loading', 'Loading tags...')}</span>
            </span>
          </div>
        ) : sortedTags.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Tag className="h-10 w-10 text-gray-400" />
            <div className="text-base font-medium text-gray-600 dark:text-gray-300">
              {searchTerm
                ? safeT('tagManager.noResults', 'No tags match your search')
                : safeT('tagManager.noTags', 'No tags created yet')}
            </div>
            <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
              {t(
                'tagManager.noTagsDescription',
                'Create tags to organize your entities and attachments'
              )}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedTags.length === sortedTags.length && sortedTags.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags(sortedTags.map((tag) => tag.id));
                        } else {
                          setSelectedTags([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-xs sm:text-sm">{safeT('tagManager.tag', 'Tag')}</span>
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    <span className="text-xs sm:text-sm">{safeT('tagManager.style', 'Style')}</span>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('usage')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-xs sm:text-sm">{safeT('tagManager.usage', 'Usage')}</span>
                      {sortColumn === 'usage' ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-xs sm:text-sm">{safeT('tagManager.created', 'Created')}</span>
                      {sortColumn === 'created' ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    <span className="text-xs sm:text-sm">{safeT('tagManager.actions', 'Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {sortedTags.map((tag) => (
                  <tr
                    key={tag.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
                    onDoubleClick={() => openEditModal(tag)}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag.id)}
                        onChange={() => toggleTagSelection(tag.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium border"
                        style={{
                          backgroundColor: tag.backgroundColor || '#F3F4F6',
                          color: tag.textColor || '#374151',
                          borderColor: tag.borderColor || '#D1D5DB',
                          fontWeight: tag.fontWeight || 'normal',
                        }}
                      >
                        <Tag className="h-3 w-3" />
                        {tag.name}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-xs">
                        <div
                          className="w-6 h-6 rounded border"
                          style={{
                            backgroundColor: tag.backgroundColor || '#F3F4F6',
                            borderColor: tag.borderColor || '#D1D5DB',
                          }}
                        />
                        <span className={textSecondary}>
                          {tag.fontWeight === 'bold' ? 'Bold' : 'Normal'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className={`text-sm ${textSecondary}`}>
                          {tag.usageCount || 0} <span className="text-xs sm:text-sm">{safeT('tagManager.uses', 'uses')}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-sm ${textSecondary}`}>
                        {tag.createdAt
                          ? new Date(tag.createdAt).toLocaleDateString()
                          : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewTagUsage(tag)}
                          className="rounded-md bg-purple-100 p-2 text-purple-600 hover:bg-purple-200 dark:bg-purple-500/20 dark:text-purple-200 dark:hover:bg-purple-500/30"
                          title={safeT('tagManager.viewUsage', 'View Usage')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(tag)}
                          className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-200 dark:hover:bg-blue-500/30"
                          title={safeT('common.edit', 'Edit')}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag.id)}
                          className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-200 dark:hover:bg-red-500/30"
                          title={safeT('common.delete', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${borderColor} ${bgPrimary} p-6 shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-semibold ${textPrimary}`}>
                {showCreateModal
                  ? safeT('tagManager.createTag', 'Create Tag')
                  : safeT('tagManager.editTag', 'Edit Tag')}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingTag(null);
                  resetTagForm();
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`text-sm font-medium ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('tagManager.tagName', 'Tag Name')}</span>
                </label>
                <input
                  type="text"
                  value={tagForm.name}
                  onChange={(e) =>
                    setTagForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500`}
                  placeholder={safeT('tagManager.tagNamePlaceholder', 'Enter tag name')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium ${textPrimary}`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.backgroundColor', 'Background Color')}</span>
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={tagForm.backgroundColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value,
                        }))
                      }
                      className="h-10 w-16 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={tagForm.backgroundColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value,
                        }))
                      }
                      className={`flex-1 rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`text-sm font-medium ${textPrimary}`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.textColor', 'Text Color')}</span>
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={tagForm.textColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({ ...prev, textColor: e.target.value }))
                      }
                      className="h-10 w-16 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={tagForm.textColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({ ...prev, textColor: e.target.value }))
                      }
                      className={`flex-1 rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium ${textPrimary}`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.borderColor', 'Border Color')}</span>
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={tagForm.borderColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({
                          ...prev,
                          borderColor: e.target.value,
                        }))
                      }
                      className="h-10 w-16 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={tagForm.borderColor}
                      onChange={(e) =>
                        setTagForm((prev) => ({
                          ...prev,
                          borderColor: e.target.value,
                        }))
                      }
                      className={`flex-1 rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`text-sm font-medium ${textPrimary}`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.fontWeight', 'Font Weight')}</span>
                  </label>
                  <select
                    value={tagForm.fontWeight}
                    onChange={(e) =>
                      setTagForm((prev) => ({ ...prev, fontWeight: e.target.value }))
                    }
                    className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                  >
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className={`text-sm font-medium ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('tagManager.preview', 'Preview')}</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium border"
                    style={{
                      backgroundColor: tagForm.backgroundColor,
                      color: tagForm.textColor,
                      borderColor: tagForm.borderColor,
                      fontWeight: tagForm.fontWeight,
                    }}
                  >
                    <Tag className="h-3 w-3" />
                    {tagForm.name || safeT('tagManager.sampleTag', 'Sample Tag')}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingTag(null);
                  resetTagForm();
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                onClick={showCreateModal ? handleCreateTag : handleUpdateTag}
                disabled={loading || !tagForm.name.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-purple-700 disabled:opacity-50"
              >
                {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
                <Save className="h-4 w-4" />
                {showCreateModal
                  ? safeT('tagManager.create', 'Create')
                  : safeT('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${borderColor} ${bgPrimary} p-6 shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-semibold ${textPrimary}`}>
                <span className="text-xs sm:text-sm">{safeT('tagManager.mergeTags', 'Merge Tags')}</span>
              </h2>
              <button
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeTarget('');
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className={`text-sm ${textSecondary} mb-3`}>
                  {t(
                    'tagManager.mergeDescription',
                    'All selected tags will be merged into the target tag. This action cannot be undone.'
                  )}
                </p>

                <label className={`text-sm font-medium ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('tagManager.selectedTags', 'Selected Tags')}</span> ({selectedTags.length})
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedTags.map((tagId) => {
                    const tag = tags.find((t) => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span
                        key={tagId}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium border"
                        style={{
                          backgroundColor: tag.backgroundColor || '#F3F4F6',
                          color: tag.textColor || '#374151',
                          borderColor: tag.borderColor || '#D1D5DB',
                          fontWeight: tag.fontWeight || 'normal',
                        }}
                      >
                        <Tag className="h-3 w-3" />
                        {tag.name}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('tagManager.targetTag', 'Target Tag')}</span>
                </label>
                <select
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500`}
                >
                  <option value="">
                    {safeT('tagManager.selectTargetTag', 'Select target tag')}
                  </option>
                  {selectedTags.map((tagId) => {
                    const tag = tags.find((t) => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <option key={tagId} value={tagId}>
                        {tag.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeTarget('');
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                onClick={handleMergeTags}
                disabled={loading || !mergeTarget}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:opacity-50"
              >
                {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
                <Merge className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('tagManager.merge', 'Merge')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Usage Modal */}
      {showUsageModal && viewingTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-4xl max-h-[80vh] rounded-2xl border ${borderColor} ${bgPrimary} shadow-xl flex flex-col`}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Eye className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className={`text-xl font-semibold ${textPrimary}`}>
                    <span className="text-xs sm:text-sm md:text-base">{safeT('tagManager.tagUsage', 'Tag Usage')}</span>
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium border"
                      style={{
                        backgroundColor: viewingTag.backgroundColor || '#F3F4F6',
                        color: viewingTag.textColor || '#374151',
                        borderColor: viewingTag.borderColor || '#D1D5DB',
                        fontWeight: viewingTag.fontWeight || 'normal',
                      }}
                    >
                      <Tag className="h-3 w-3" />
                      {viewingTag.name}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowUsageModal(false);
                  setViewingTag(null);
                  setTagUsageData(null);
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingUsage ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
                  <span className="ml-2 text-sm text-purple-600">
                    <span className="text-xs sm:text-sm">{safeT('tagManager.loadingUsage', 'Loading usage data...')}</span>
                  </span>
                </div>
              ) : !tagUsageData || tagUsageData.totalCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mb-3" />
                  <p className={`text-base font-medium ${textPrimary}`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.noUsage', 'This tag is not currently used')}</span>
                  </p>
                  <p className={`text-sm ${textSecondary} mt-1`}>
                    <span className="text-xs sm:text-sm">{safeT('tagManager.noUsageDescription', 'No items are associated with this tag')}</span>
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className={`rounded-xl border ${borderColor} ${bgSecondary} p-4`}>
                    <h3 className={`text-sm font-semibold ${textPrimary} mb-3`}>
                      <span className="text-xs sm:text-sm">{safeT('tagManager.usageSummary', 'Usage Summary')}</span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className={textSecondary}>Total:</span>
                        <span className={`ml-2 font-semibold ${textPrimary}`}>{tagUsageData.totalCount}</span>
                      </div>
                      {tagUsageData.entities?.length > 0 && (
                        <div>
                          <span className={textSecondary}>Entities:</span>
                          <span className={`ml-2 font-semibold ${textPrimary}`}>{tagUsageData.entities.length}</span>
                        </div>
                      )}
                      {tagUsageData.attachments?.length > 0 && (
                        <div>
                          <span className={textSecondary}>Attachments:</span>
                          <span className={`ml-2 font-semibold ${textPrimary}`}>{tagUsageData.attachments.length}</span>
                        </div>
                      )}
                      {tagUsageData.salesNotes?.length > 0 && (
                        <div>
                          <span className={textSecondary}>Notes:</span>
                          <span className={`ml-2 font-semibold ${textPrimary}`}>{tagUsageData.salesNotes.length}</span>
                        </div>
                      )}
                      {tagUsageData.knowledge?.length > 0 && (
                        <div>
                          <span className={textSecondary}>Knowledge:</span>
                          <span className={`ml-2 font-semibold ${textPrimary}`}>{tagUsageData.knowledge.length}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Entities Section */}
                  {tagUsageData.entities?.length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
                        <Layers className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">{safeT('tagManager.entities', 'Entities')}</span> ({tagUsageData.entities.length})
                      </h3>
                      <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {tagUsageData.entities.map((item, idx) => (
                            <div
                              key={idx}
                              className={`p-3 ${bgPrimary} hover:bg-gray-50 dark:hover:bg-gray-800/60 flex items-center justify-between`}
                            >
                              <div>
                                <div className={`text-sm font-medium ${textPrimary}`}>
                                  {item.displayName || item.entityId}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {item.entityType} • ID: {item.entityId}
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 text-gray-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Attachments Section */}
                  {tagUsageData.attachments?.length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
                        <FileText className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">{safeT('tagManager.attachments', 'Attachments')}</span> ({tagUsageData.attachments.length})
                      </h3>
                      <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {tagUsageData.attachments.map((item, idx) => (
                            <div
                              key={idx}
                              className={`p-3 ${bgPrimary} hover:bg-gray-50 dark:hover:bg-gray-800/60 flex items-center justify-between`}
                            >
                              <div>
                                <div className={`text-sm font-medium ${textPrimary}`}>
                                  {item.displayName || item.entityId}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {item.entityId}
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 text-gray-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes Section */}
                  {tagUsageData.salesNotes?.length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
                        <FileText className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">{safeT('tagManager.salesNotes', 'Notes')}</span> ({tagUsageData.salesNotes.length})
                      </h3>
                      <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {tagUsageData.salesNotes.map((item, idx) => (
                            <div
                              key={idx}
                              className={`p-3 ${bgPrimary} hover:bg-gray-50 dark:hover:bg-gray-800/60 flex items-center justify-between`}
                            >
                              <div>
                                <div className={`text-sm font-medium ${textPrimary}`}>
                                  {item.displayName || item.entityId}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {item.entityId}
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 text-gray-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Knowledge Section */}
                  {tagUsageData.knowledge?.length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
                        <FileText className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">{safeT('tagManager.knowledge', 'Knowledge Articles')}</span> ({tagUsageData.knowledge.length})
                      </h3>
                      <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {tagUsageData.knowledge.map((item, idx) => (
                            <div
                              key={idx}
                              className={`p-3 ${bgPrimary} hover:bg-gray-50 dark:hover:bg-gray-800/60 flex items-center justify-between`}
                            >
                              <div>
                                <div className={`text-sm font-medium ${textPrimary}`}>
                                  {item.displayName || item.entityId}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {item.entityId}
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 text-gray-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowUsageModal(false);
                  setViewingTag(null);
                  setTagUsageData(null);
                }}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.close', 'Close')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {activeTab === 'autoRules' && (
        <div className="space-y-6">
          {/* Auto Rules Actions */}
          <div className={`rounded-xl border ${borderColor} ${bgPrimary} p-4 shadow-sm`}>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => openAutoRuleModal()}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('tagManager.createRule', 'Create Rule')}</span>
              </button>

              <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2">
                <span className="text-xs text-slate-500">{safeT('tagManager.runFor', 'Run for')}</span>
                <select
                  value={autoRunEntityType}
                  onChange={(e) => setAutoRunEntityType(e.target.value)}
                  className="bg-transparent text-sm outline-none"
                >
                  {entityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleRunAutoRules}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('tagManager.runNow', 'Run Now')}</span>
              </button>
              <button
                onClick={loadAutoRules}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-gray-700"
              >
                <RefreshCw className={`h-4 w-4 ${autoRuleLoading ? 'animate-spin' : ''}`} />
                <span className="text-xs sm:text-sm">{safeT('common.refresh', 'Refresh')}</span>
              </button>

              <div className="ml-auto inline-flex items-center gap-2">
                <span className="text-xs text-slate-500">{safeT('tagManager.filterEntity', 'Filter')}</span>
                <select
                  value={autoRuleEntityFilter}
                  onChange={(e) => setAutoRuleEntityFilter(e.target.value)}
                  className="bg-transparent text-sm outline-none"
                >
                  <option value="All">{safeT('tagManager.allEntities', 'All')}</option>
                  {entityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Auto Rules List */}
          <div className={`rounded-xl border ${borderColor} ${bgPrimary} shadow-sm overflow-hidden`}>
            {autoRuleLoading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-indigo-600" />
                <span className="ml-2 text-sm text-indigo-600">
                  {safeT('tagManager.loadingRules', 'Loading rules...')}
                </span>
              </div>
            ) : filteredAutoRules.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Tag className="h-10 w-10 text-gray-400" />
                <div className="text-base font-medium text-gray-600 dark:text-gray-300">
                  {safeT('tagManager.noRules', 'No auto tag rules yet')}
                </div>
                <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
                  {safeT('tagManager.noRulesDescription', 'Create rules to automatically apply tags based on conditions.')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {safeT('tagManager.ruleName', 'Rule')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {safeT('tagManager.entity', 'Entity')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {safeT('tagManager.ruleTags', 'Tags')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {safeT('tagManager.enabled', 'Enabled')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {safeT('tagManager.actions', 'Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {filteredAutoRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {rule.name}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {entityOptions.find((opt) => opt.value === rule.entityType)?.label || rule.entityType}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {(rule.tags || []).join(', ') || '-'}
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => handleToggleAutoRule(rule)}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                              rule.enabled
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {rule.enabled ? safeT('tagManager.enabled', 'Enabled') : safeT('tagManager.disabled', 'Disabled')}
                          </button>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openAutoRuleModal(rule)}
                              className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-200 dark:hover:bg-blue-500/30"
                              title={safeT('common.edit', 'Edit')}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAutoRule(rule.id)}
                              className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-200 dark:hover:bg-red-500/30"
                              title={safeT('common.delete', 'Delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Auto Rule Modal */}
          {showAutoRuleModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className={`w-full max-w-3xl rounded-2xl border ${borderColor} ${bgPrimary} p-6 shadow-xl max-h-[85vh] overflow-y-auto`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-xl font-semibold ${textPrimary}`}>
                    {editingAutoRule ? safeT('tagManager.editRule', 'Edit Rule') : safeT('tagManager.createRule', 'Create Rule')}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAutoRuleModal(false);
                      setEditingAutoRule(null);
                      resetAutoRuleForm();
                    }}
                    className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`text-sm font-medium ${textPrimary}`}>
                        {safeT('tagManager.ruleName', 'Rule Name')}
                      </label>
                      <input
                        type="text"
                        value={autoRuleForm.name}
                        onChange={(e) => setAutoRuleForm((prev) => ({ ...prev, name: e.target.value }))}
                        className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                        placeholder={safeT('tagManager.ruleNamePlaceholder', 'Enter rule name')}
                      />
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${textPrimary}`}>
                        {safeT('tagManager.entity', 'Entity')}
                      </label>
                      <select
                        value={autoRuleForm.entityType}
                        onChange={(e) =>
                          setAutoRuleForm((prev) => ({ ...prev, entityType: e.target.value }))
                        }
                        className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                      >
                        {entityOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`text-sm font-medium ${textPrimary}`}>
                      {safeT('tagManager.ruleTags', 'Tags (comma separated)')}
                    </label>
                    <input
                      type="text"
                      value={autoRuleTagsInput}
                      onChange={(e) => setAutoRuleTagsInput(e.target.value)}
                      className={`mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} ${textPrimary} px-3 py-2 text-sm`}
                      placeholder={safeT('tagManager.tagsPlaceholder', 'e.g. high-value, urgent')}
                    />
                  </div>

                  <div>
                    <label className={`text-sm font-medium ${textPrimary}`}>
                      {safeT('tagManager.conditions', 'Conditions')}
                    </label>
                    <div className="mt-2">
                      {renderConditionGroup(autoRuleForm.conditions || createEmptyGroup(), [])}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={autoRuleForm.enabled}
                      onChange={(e) => setAutoRuleForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                    />
                    <span className={`text-sm ${textSecondary}`}>
                      {safeT('tagManager.enabled', 'Enabled')}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAutoRuleModal(false);
                      setEditingAutoRule(null);
                      resetAutoRuleForm();
                    }}
                    className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {safeT('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleSaveAutoRule}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700"
                  >
                    <Save className="h-4 w-4" />
                    {safeT('common.save', 'Save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagManager;

